import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './WorldTierModal.css';
import TooltipBadge from './shared/TooltipBadge';

const TIER_COLORS = {
  0: '#aaaaaa',
  1: '#4fc3f7',
  2: '#66bb6a',
  3: '#ffa726',
  4: '#ab47bc',
  5: '#ef5350',
};

const TIER_ICONS = {
  0: '',
  1: '⚔️',
  2: '🛡️',
  3: '🏆',
  4: '💎',
  5: '🔥',
};

export default function WorldTierModal({ character, onClose, onTierChanged, onOpenGuide }) {
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/world-tier/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTierData(res.data);
    } catch (err) {
      console.error('Failed to fetch world tier status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleAdvance = async () => {
    // v643.2: confirm tradeoff before locking in a higher tier
    const next = nextConfig;
    if (next) {
      const msg =
        `Unlock ${next.name} (Tier ${nextUnlockable})?\n\n` +
        `• Monster HP ×${next.hpMult}\n` +
        `• XP ×${next.xpMult}\n` +
        `• Gold ×${next.goldMult}\n` +
        `• Magic Find +${next.magicFindBonus}\n\n` +
        `Enemies hit harder, but rewards scale up too.`;
      if (!window.confirm(msg)) return;
    }
    setActionLoading(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/world-tier/advance', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: res.data.message });
      await fetchStatus();
      if (onTierChanged) onTierChanged(res.data.newTier);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to advance tier' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetTier = async (tier) => {
    if (tier === tierData?.currentTier) return;
    // v643.2: confirm before switching to a tier with non-default modifiers
    const target = tierData?.allTiers?.find(t => t.id === tier);
    if (target && tier > 0) {
      const msg =
        `Switch to ${target.name} (Tier ${tier})?\n\n` +
        `• Monster HP ×${target.hpMult}\n` +
        `• XP ×${target.xpMult}\n` +
        `• Gold ×${target.goldMult}\n` +
        `• Magic Find +${target.magicFindBonus}`;
      if (!window.confirm(msg)) return;
    }
    setActionLoading(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/world-tier/set', { tier }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `Switched to ${res.data.config.name} tier.` });
      await fetchStatus();
      if (onTierChanged) onTierChanged(tier);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to switch tier' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="wt-modal-overlay" onClick={onClose}>
        <div className="wt-modal" onClick={e => e.stopPropagation()}>
          <div className="wt-loading">Loading...</div>
        </div>
      </div>
    );
  }

  const { currentTier, highestTier, allTiers, nextUnlockable, nextConfig, maxCharLevel } = tierData || {};

  return (
    <div className="wt-modal-overlay" onClick={onClose}>
      <div className="wt-modal" onClick={e => e.stopPropagation()}>
        <div className="wt-header">
          <h2>World Tier<TooltipBadge id="world.tier" /></h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {onOpenGuide && (
              <button
                type="button"
                onClick={() => onOpenGuide('world-tiers')}
                style={{
                  background: 'rgba(79, 195, 247, 0.12)',
                  border: '1px solid rgba(79, 195, 247, 0.28)',
                  color: '#9fdcf7',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  cursor: 'pointer'
                }}
              >
                Guide
              </button>
            )}
            <button className="wt-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="wt-current">
          <span className="wt-current-icon">{TIER_ICONS[currentTier] || ''}</span>
          <span className="wt-current-name" style={{ color: TIER_COLORS[currentTier] }}>
            {allTiers?.find(t => t.id === currentTier)?.name || 'Normal'}
          </span>
          <span className="wt-current-label">Tier {currentTier}</span>
        </div>

        {message && (
          <div className={`wt-message wt-message-${message.type}`}>{message.text}</div>
        )}

        <div className="wt-tier-list">
          {allTiers?.map(tier => {
            const isActive = tier.id === currentTier;
            const isUnlocked = tier.id <= highestTier;
            const isNext = tier.id === nextUnlockable;
            const isLocked = !isUnlocked && !isNext;

            return (
              <div
                key={tier.id}
                className={`wt-tier-card ${isActive ? 'wt-active' : ''} ${isLocked ? 'wt-locked' : ''} ${isNext ? 'wt-next' : ''}`}
                style={{ borderColor: isActive ? TIER_COLORS[tier.id] : undefined }}
              >
                <div className="wt-tier-card-header">
                  <span className="wt-tier-icon">{tier.icon}</span>
                  <span className="wt-tier-name" style={{ color: TIER_COLORS[tier.id] }}>
                    {tier.name}
                  </span>
                  <span className="wt-tier-id">T{tier.id}</span>
                  {isActive && <span className="wt-tier-badge">ACTIVE</span>}
                </div>

                <div className="wt-tier-stats">
                  <div className="wt-stat">
                    <span className="wt-stat-label">Monster HP</span>
                    <span className="wt-stat-value wt-stat-danger">×{tier.hpMult}</span>
                  </div>
                  <div className="wt-stat">
                    <span className="wt-stat-label">XP Bonus</span>
                    <span className="wt-stat-value wt-stat-good">×{tier.xpMult}</span>
                  </div>
                  <div className="wt-stat">
                    <span className="wt-stat-label">Gold Bonus</span>
                    <span className="wt-stat-value wt-stat-good">×{tier.goldMult}</span>
                  </div>
                  <div className="wt-stat">
                    <span className="wt-stat-label">Magic Find</span>
                    <span className="wt-stat-value wt-stat-good">+{tier.magicFindBonus}</span>
                  </div>
                </div>

                <div className="wt-tier-req">
                  {tier.id === 0 ? 'Default' : `Requires Level ${tier.levelReq}`}
                  {isLocked && maxCharLevel < tier.levelReq && (
                    <span className="wt-req-gap"> (your max: {maxCharLevel})</span>
                  )}
                </div>

                <div className="wt-tier-actions">
                  {isActive && (
                    <span className="wt-active-label">Currently Active</span>
                  )}
                  {isUnlocked && !isActive && (
                    <button
                      className="wt-btn wt-btn-switch"
                      onClick={() => handleSetTier(tier.id)}
                      disabled={actionLoading}
                    >
                      Switch to {tier.name}
                    </button>
                  )}
                  {isNext && (
                    <button
                      className="wt-btn wt-btn-advance"
                      onClick={handleAdvance}
                      disabled={actionLoading}
                      style={{ borderColor: TIER_COLORS[tier.id], color: TIER_COLORS[tier.id] }}
                    >
                      {actionLoading ? 'Advancing...' : `Unlock ${tier.name}`}
                    </button>
                  )}
                  {isLocked && !isNext && (
                    <span className="wt-locked-label">🔒 Locked</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
